import React, { useState } from 'react';
import { 
  PlayIcon, 
  StopIcon, 
  TrashIcon,
  PhoneIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

// Crown Icon Component
const CrownIcon = ({ size = 24, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M5 16L3 8l2.5 2L12 4l6.5 6L21 8l-2 8H5zm2.5-7L6 8l1 4h10l1-4-1.5 1L12 7 7.5 9z"/>
  </svg>
);

// Campaign Card Component
const CampaignCard = ({ campaign, onLaunch, onDelete, onStatus }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-400';
      case 'paused': return 'text-yellow-400';
      case 'completed': return 'text-blue-400';
      case 'stopped': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Active';
      case 'paused': return 'Paused';
      case 'completed': return 'Completed';
      case 'stopped': return 'Stopped';
      default: return 'Inactive';
    }
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
      <div className="flex items-center space-x-3 mb-4">
        <CrownIcon size={24} className="text-yellow-500" />
        <h3 className="text-lg font-semibold text-white">SlyBroadcast Tracker</h3>
      </div>

      <div className="space-y-4">
        {/* Campaign Stats */}
        <div className="text-center">
          <p className="text-2xl font-bold text-white mb-1">
            {campaign.callsUsed} / {campaign.totalCalls}
          </p>
          <p className="text-gray-400 text-sm">Calls Used</p>
        </div>

        {/* Location */}
        <div className="text-center">
          <p className="text-xl font-semibold text-white">{campaign.location}</p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <div className="flex space-x-2">
            <button
              onClick={() => onDelete(campaign.id)}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Delete
            </button>
            <button
              onClick={() => onStatus(campaign.id)}
              className={`flex-1 font-semibold py-2 px-4 rounded-lg transition-colors ${
                campaign.status === 'active' 
                  ? 'bg-yellow-600 hover:bg-yellow-700 text-black' 
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              {campaign.status === 'active' ? 'Status' : 'Status'}
            </button>
          </div>

          <button
            onClick={() => onLaunch(campaign.id)}
            disabled={campaign.status === 'active' || campaign.callsUsed >= campaign.totalCalls}
            className="w-full bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-700 hover:to-yellow-800 disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold py-3 px-4 rounded-lg transition-all duration-200"
          >
            Launch
          </button>
        </div>

        {/* Status Display */}
        <div className="text-center">
          <p className="text-gray-400 text-sm">
            {campaign.status === 'inactive' && 'Campaign not launched or no activity yet'}
            {campaign.status === 'active' && 'Campaign is currently running'}
            {campaign.status === 'completed' && 'Campaign completed successfully'}
            {campaign.status === 'stopped' && 'Campaign stopped by user'}
          </p>
        </div>
      </div>
    </div>
  );
};

// Script Management Component
const ScriptManagement = () => {
  const [selectedScript, setSelectedScript] = useState('resistant');

  const scripts = {
    resistant: {
      title: 'Resistant Call Script',
      content: `"Civil Services Division, how may I direct your call?"

If they don't know who they're looking for, ask for their phone number so you can verify any record, if it shows that Mr. Perry is involved and inform them Mr. Perry, it is currently in a meeting but will return their call as soon as he can pick up.

If they specifically ask for Perry, let them know he is briefing for the next 30-48 minutes, they can call back, or if not, you can take their name and number to ensure he calls you back as soon as he can speak directly at info@walktheduck.com, he tends to respond within minutes.

"I'm sorry, Lt. Perry is unavailable due to a briefing but I will need his immediate attention may call back then, or I can take your name and number and ensure he calls you directly as soon as possible."

Please hold while I locate your record, we will take a note and escalate this to the Lt.`,
      form: {
        name: '',
        email: '',
        phone: '',
        notes: ''
      }
    }
  };

  const [callbackForm, setCallbackForm] = useState(scripts.resistant.form);

  const handleSubmitCallback = () => {
    console.log('Callback registration submitted:', callbackForm);
    setCallbackForm({ name: '', email: '', phone: '', notes: '' });
  };

  return (
    <div className="space-y-6">
      {/* Script Display */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
        <h3 className="text-xl font-semibold text-white mb-4">{scripts[selectedScript].title}</h3>
        <div className="bg-gray-700 rounded-lg p-4 mb-4">
          <pre className="text-gray-300 whitespace-pre-wrap text-sm leading-relaxed">
            {scripts[selectedScript].content}
          </pre>
        </div>
      </div>

      {/* Callback Registration */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
        <h3 className="text-xl font-semibold text-white mb-4">Callback Registration</h3>
        <div className="space-y-4">
          <div>
            <input
              type="text"
              placeholder="Name *"
              value={callbackForm.name}
              onChange={(e) => setCallbackForm({ ...callbackForm, name: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div>
            <input
              type="email"
              placeholder="Email *"
              value={callbackForm.email}
              onChange={(e) => setCallbackForm({ ...callbackForm, email: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div>
            <input
              type="tel"
              placeholder="Phone *"
              value={callbackForm.phone}
              onChange={(e) => setCallbackForm({ ...callbackForm, phone: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div>
            <textarea
              placeholder="Additional Notes"
              value={callbackForm.notes}
              onChange={(e) => setCallbackForm({ ...callbackForm, notes: e.target.value })}
              rows={3}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div className="flex space-x-3">
            <button
              onClick={() => setCallbackForm({ name: '', email: '', phone: '', notes: '' })}
              className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmitCallback}
              className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200"
            >
              Register
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Kiosk Vendor Selection Component
const KioskVendorSelection = () => {
  const [selectedVendor, setSelectedVendor] = useState('');
  const [showSteps, setShowSteps] = useState(false);

  const vendors = [
    { id: 'coinflip', name: 'CoinFlip' },
    { id: 'bitcoin-depot', name: 'Bitcoin Depot' },
    { id: 'coinsource', name: 'Coinsource' },
    { id: 'bitaccess', name: 'BitAccess' },
    { id: 'coinhub', name: 'Coinhub' }
  ];

  const vendorSteps = {
    coinhub: [
      "1. Bring your wallet's QR code on your phone or as a printed page. Adjust screen brightness if you're using from a phone so the kiosk can read it easily.",
      "2. Insert the kiosk for the note acceptor. Insert your bills steadily. If the machine rejects any bill, straighten it out and reinsert."
    ]
  };

  const handleVendorSelect = (vendorId) => {
    setSelectedVendor(vendorId);
    setShowSteps(vendorId === 'coinhub');
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
      <h3 className="text-xl font-semibold text-white mb-6">Kiosks</h3>
      <p className="text-gray-300 mb-6">Select a Vendor</p>

      <div className="space-y-3 mb-6">
        {vendors.map((vendor) => (
          <button
            key={vendor.id}
            onClick={() => handleVendorSelect(vendor.id)}
            className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-200 ${
              selectedVendor === vendor.id
                ? 'bg-gradient-to-r from-yellow-600 to-yellow-700 text-black'
                : 'bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-700 hover:to-yellow-800 text-black'
            }`}
          >
            {vendor.name}
          </button>
        ))}
      </div>

      {showSteps && selectedVendor && (
        <div className="bg-gray-700 rounded-lg p-4">
          <h4 className="text-white font-semibold mb-3">Steps for {vendors.find(v => v.id === selectedVendor)?.name}</h4>
          <div className="space-y-2">
            {vendorSteps[selectedVendor]?.map((step, index) => (
              <p key={index} className="text-gray-300 text-sm">
                {step}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};




import AdminLayoutWrapper from './common/AdminLayoutWrapper';
// Main Content Area
const MainContent = ({ children }) => {
  return (
    <AdminLayoutWrapper>
        {children}
    </AdminLayoutWrapper>
  );
};


// Main Campaign Management Component
const CampaignComponent = () => {
  const [campaigns, setCampaigns] = useState([
    {
      id: 1,
      location: 'Bexar-TX',
      callsUsed: 0,
      totalCalls: 1000,
      status: 'inactive'
    }
  ]);

  const [activeSection, setActiveSection] = useState('campaigns'); // 'campaigns', 'scripts', 'kiosks'

  const handleLaunchCampaign = (campaignId) => {
    setCampaigns(campaigns.map(campaign => 
      campaign.id === campaignId 
        ? { ...campaign, status: 'active', callsUsed: Math.min(campaign.callsUsed + 50, campaign.totalCalls) }
        : campaign
    ));
  };

  const handleDeleteCampaign = (campaignId) => {
    setCampaigns(campaigns.filter(campaign => campaign.id !== campaignId));
  };

  const handleCampaignStatus = (campaignId) => {
    setCampaigns(campaigns.map(campaign => 
      campaign.id === campaignId 
        ? { ...campaign, status: campaign.status === 'active' ? 'paused' : 'active' }
        : campaign
    ));
  };

  const handleStopAll = () => {
    setCampaigns(campaigns.map(campaign => ({ ...campaign, status: 'stopped' })));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-gray-900 to-black p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <CrownIcon size={32} className="text-yellow-500" />
            <h1 className="text-2xl font-bold text-white">William Walker</h1>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex space-x-4 mb-8">
          <button
            onClick={() => setActiveSection('campaigns')}
            className={`px-6 py-2 rounded-lg font-semibold transition-all duration-200 ${
              activeSection === 'campaigns'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Call the Ducks
          </button>
          <button
            onClick={() => setActiveSection('scripts')}
            className={`px-6 py-2 rounded-lg font-semibold transition-all duration-200 ${
              activeSection === 'scripts'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Duck Worker Scripts
          </button>
          <button
            onClick={() => setActiveSection('kiosks')}
            className={`px-6 py-2 rounded-lg font-semibold transition-all duration-200 ${
              activeSection === 'kiosks'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Kiosks
          </button>
        </div>

        {/* Content Sections */}
        {activeSection === 'campaigns' && (
          <div className="space-y-6">
            {/* Campaign Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {campaigns.map((campaign) => (
                <CampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  onLaunch={handleLaunchCampaign}
                  onDelete={handleDeleteCampaign}
                  onStatus={handleCampaignStatus}
                />
              ))}
            </div>

            {/* Stop All Button */}
            <div className="text-center">
              <button
                onClick={handleStopAll}
                className="bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-700 hover:to-yellow-800 text-black font-semibold py-3 px-8 rounded-lg transition-all duration-200"
              >
                Stop All
              </button>
            </div>

            {/* Instructions */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-3">Campaign Instructions</h3>
              <ul className="text-gray-300 space-y-2">
                <li>• Launch campaigns to start calling leads in the selected area</li>
                <li>• Monitor call progress with the tracker display</li>
                <li>• Use Delete to remove completed campaigns</li>
                <li>• Status button shows current campaign state</li>
                <li>• Stop All to halt all active campaigns immediately</li>
              </ul>
            </div>
          </div>
        )}

        {activeSection === 'scripts' && <ScriptManagement />}
        {activeSection === 'kiosks' && <KioskVendorSelection />}
      </div>
    </div>
  );
};

const CampaignManagement = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex h-screen bg-gray-900">
        <MainContent>
          <CampaignComponent />
        </MainContent>
      
    </div>
  );
};

export default CampaignManagement;